import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

// Vectorized version of Piyush Soni's handwritten signature.
// Original raster tracing captured with potrace; the path lives in
// potrace's Y-flipped coordinate space, which is why we wrap it with
// `transform="translate(0,970) scale(0.1,-0.1)"` on the SVG group.
// The path was traced from the supplied handwritten signature with potrace
// and hand-cleaned to drop four tiny ink-speckles that were sitting to the
// left of the "P" swash in the original scan (they read as stray noise dots
// once vectorized). Coordinates are in potrace's y-flipped space; the
// wrapping `<g>` in the JSX flips them back with translate/scale.
const SIGNATURE_PATH =
  "M880 753 c-322 -31 -615 -201 -643 -373 -6 -32 -5 -31 8 7 57 175 302 314 618 353 140 17 286 -9 303 -55 24 -61 -127 -208 -311 -303 -140 -72 -210 -98 -167 -63 12 11 18 21 13 23 -5 1 6 47 25 101 19 55 34 106 34 115 -1 10 -18 -34 -40 -96 -52 -152 -66 -173 -120 -189 -24 -7 -48 -17 -54 -23 -15 -15 5 -13 47 5 20 8 39 15 42 15 7 0 -32 -77 -57 -113 -11 -16 -18 -31 -15 -33 3 -3 27 31 54 76 48 81 48 81 153 129 58 26 124 59 148 74 23 15 42 24 42 20 0 -4 6 0 13 8 6 9 42 39 79 67 70 56 128 131 128 168 0 65 -132 104 -300 87z M1610 629 c-38 -15 -80 -60 -80 -84 0 -12 14 -39 30 -60 39 -49 38 -51 -16 -64 -64 -16 -124 -48 -124 -66 0 -8 8 -15 17 -15 44 0 -11 -14 -188 -46 -107 -19 -202 -37 -211 -40 -15 -4 -15 -1 2 43 10 26 21 64 25 83 7 35 7 35 -25 0 -31 -35 -31 -35 -25 -5 6 30 6 30 -20 -2 -30 -37 -63 -47 -45 -14 15 29 12 39 -5 17 -15 -19 -21 -56 -9 -56 3 0 15 5 27 12 27 14 77 16 77 3 0 -5 -9 -28 -19 -50 -16 -36 -24 -42 -78 -57 -94 -27 -160 -105 -104 -123 37 -11 171 87 171 125 0 15 402 86 764 136 137 18 251 36 254 39 9 8 -15 6 -233 -25 -252 -36 -240 -36 -210 -5 33 32 31 52 -10 111 -42 59 -42 63 -7 96 36 36 76 50 92 35 10 -9 11 -9 8 3 -6 19 -26 22 -58 9z m-12 -221 c-10 -29 -71 -58 -122 -58 -46 0 -49 1 -34 16 33 33 164 68 156 42z m-608 -184 c0 -30 -94 -104 -132 -104 -35 0 -36 19 -2 51 30 28 134 70 134 53z M1278 510 c-8 -19 -20 -52 -27 -73 -12 -36 -42 -63 -55 -50 -3 3 -1 12 5 20 24 28 -6 11 -33 -19 -29 -33 -29 -33 -23 -3 6 30 6 30 -20 0 -25 -29 -26 -29 -19 -5 6 22 5 23 -10 11 -9 -7 -16 -19 -16 -27 0 -18 20 -28 32 -16 6 6 17 8 25 5 7 -3 24 3 36 13 19 17 20 17 8 1 -22 -28 -4 -33 24 -7 23 22 25 22 25 4 1 -18 2 -17 19 4 23 26 30 27 46 6 11 -15 14 -15 43 0 40 21 41 28 2 13 -25 -10 -32 -9 -43 7 -8 11 -19 16 -25 12 -18 -11 -14 17 8 70 11 26 19 52 17 58 -2 6 -11 -5 -19 -24z M1810 509 c0 -5 5 -7 10 -4 6 3 10 8 10 11 0 2 -4 4 -10 4 -5 0 -10 -5 -10 -11z M1790 445 c-15 -28 -40 -34 -40 -9 0 10 -6 14 -15 10 -8 -3 -15 -2 -15 2 0 4 -14 7 -32 6 -33 -1 -66 -34 -53 -54 10 -17 25 -11 37 15 6 14 17 25 25 25 11 0 11 -5 3 -20 -13 -25 5 -27 19 -2 9 14 12 15 20 3 18 -28 59 -20 65 13 8 38 3 42 -14 11z m-120 -19 c0 -3 -4 -8 -10 -11 -5 -3 -10 -1 -10 4 0 6 5 11 10 11 6 0 10 -2 10 -4z";

// The path was traced in potrace's y-flipped coord space (0.1× scale of the
// values you see below). We keep the native units and use a tight viewBox
// around the actual ink so the reveal starts immediately.
const VB_X = 200;
const VB_Y = 70;
const VB_W = 1860;
const VB_H = 720;
const H_FULL = 970; // full path height before y-flip
const DRAW_MS = 2000;

export default function SignOff() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px -15% 0px" });
  const [drawn, setDrawn] = useState(false);
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!inView) return;
    // Sync the "signing off" fade with the end of the reveal.
    const t = setTimeout(() => setDrawn(true), reducedMotion ? 0 : DRAW_MS);
    return () => clearTimeout(t);
  }, [inView, reducedMotion]);

  return (
    <div
      ref={ref}
      data-testid="sign-off"
      className="relative mt-16 flex w-full flex-col items-center justify-center px-6 md:mt-24"
      aria-label="Piyush Soni signing off"
    >
      <svg
        viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`}
        role="img"
        aria-label="Piyush Soni handwritten signature"
        className="h-auto w-[240px] text-[#f2f0e6] sm:w-[280px] md:w-[340px]"
        style={{ overflow: "visible" }}
      >
        <defs>
          <clipPath id="sig-reveal-clip">
            <motion.rect
              x={VB_X}
              y={VB_Y}
              height={VB_H}
              initial={{ width: 0 }}
              animate={inView ? { width: VB_W } : { width: 0 }}
              transition={{
                duration: reducedMotion ? 0 : DRAW_MS / 1000,
                ease: [0.6, 0.02, 0.35, 1],
              }}
            />
          </clipPath>
        </defs>

        {/* Very faint ghost so the ink looks like it's landing on paper */}
        <g
          transform={`translate(0,${H_FULL}) scale(1,-1)`}
          fill="currentColor"
          opacity="0.06"
          aria-hidden="true"
        >
          <path d={SIGNATURE_PATH} fillRule="evenodd" />
        </g>

        {/* Revealed ink — clipped by the animating rect */}
        <g
          transform={`translate(0,${H_FULL}) scale(1,-1)`}
          fill="currentColor"
          clipPath="url(#sig-reveal-clip)"
        >
          <path d={SIGNATURE_PATH} fillRule="evenodd" />
        </g>

        {/* Traveling ink-tip glow that rides the reveal edge — feels like a nib */}
        {!reducedMotion && (
          <motion.g
            initial={{ x: VB_X, opacity: 0 }}
            animate={
              inView
                ? { x: VB_X + VB_W, opacity: [0, 1, 1, 0] }
                : { x: VB_X, opacity: 0 }
            }
            transition={{
              x: { duration: DRAW_MS / 1000, ease: [0.6, 0.02, 0.35, 1] },
              opacity: {
                duration: DRAW_MS / 1000,
                times: [0, 0.05, 0.9, 1],
                ease: "linear",
              },
            }}
            aria-hidden="true"
          >
            <circle cx="0" cy={VB_Y + VB_H * 0.55} r="12" fill="#10b981" />
            <circle
              cx="0"
              cy={VB_Y + VB_H * 0.55}
              r="38"
              fill="#10b981"
              opacity="0.35"
            />
          </motion.g>
        )}
      </svg>

      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={drawn ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid="sign-off-text"
        className="mt-5 text-xs uppercase tracking-[0.42em] text-[#7c7a72]"
      >
        Signing off.
      </motion.p>
    </div>
  );
}
